// File: src/pages/Invoices/OperationalInvoiceIntelligence.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import api from "@/lib/axios";

import OperationalHeader from "@/components/Invoices/OperationalHeader";
import OperationalLifecycle from "@/components/Invoices/OperationalLifecycle";
import OperationalTimeline from "@/components/Invoices/OperationalTimeline";
import OperationalFinancePanel from "@/components/Invoices/OperationalFinancePanel";
import OperationalAIInsights from "@/components/Invoices/OperationalAIInsights";
import OperationalPOInvoiceChain from "@/components/Invoices/OperationalPOInvoiceChain";

function getTenantParams() {
  const companyId =
    localStorage.getItem("company_id") || localStorage.getItem("companyId");
  const unitId = localStorage.getItem("unit_id") || localStorage.getItem("unitId");

  const params = {};
  if (companyId) params.company_id = companyId;
  if (unitId) params.unit_id = unitId;
  return params;
}

export default function OperationalInvoiceIntelligence() {
  const navigate = useNavigate();
  const { invoiceId } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [poChain, setPoChain] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!invoiceId) return;

      setLoading(true);
      setError("");

      try {
        const params = getTenantParams();

        const [detailRes, timelineRes, poChainRes] = await Promise.all([
          api.get(`/solo-mdsh-api/invoice/history/${invoiceId}`, { params }),
          api.get(`/solo-mdsh-api/invoice/history/${invoiceId}/timeline`, { params }),
          api.get(`/solo-mdsh-api/invoice/history/${invoiceId}/po-chain`, { params }),
        ]);

        const timelinePayload = timelineRes.data;

        setDetail(detailRes.data || null);
        setTimeline(
          Array.isArray(timelinePayload)
            ? timelinePayload
            : Array.isArray(timelinePayload?.timeline)
              ? timelinePayload.timeline
              : []
        );
        setPoChain(poChainRes.data || null);
      } catch (err) {
        console.error("Operational Intelligence load error:", err);
        setError(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Failed to load Operational Intelligence."
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [invoiceId]);

  const invoice = detail?.invoice || null;

  if (loading) {
    return (
      <div className="min-h-screen p-6 text-white">
        <div className="flex items-center gap-2 text-blue-300">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading Operational Intelligence...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen p-6 text-white">
        <div className="flex items-center gap-2 text-red-300">
          <AlertTriangle className="h-5 w-5" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen space-y-6 bg-background p-6 text-white">
      <OperationalHeader
        invoice={invoice}
        onBack={() => navigate("/invoice/history")}
        onReview={() => navigate(`/invoice/review/${invoiceId}`)}
        onViewInvoice={() => navigate(`/invoice/view/${invoiceId}`)}
      />

      <OperationalPOInvoiceChain
        chain={poChain}
        currentInvoiceId={invoiceId}
        onOpenInvoice={(id) => navigate(`/invoice/detail/${id}`)}
      />

      <OperationalLifecycle invoice={invoice} />

      <OperationalFinancePanel invoice={invoice} />

      <OperationalAIInsights invoice={invoice} timeline={timeline} />

      <OperationalTimeline invoice={invoice} timeline={timeline} />
    </div>
  );
}